import { useCallback, useState } from 'react'
import { t } from '@stokr/components-library'
import { AXIS_CHART_HEIGHT, ChartFootnote, ChartIdentity, RangePicker } from 'components/sto/chart'
import { PriceChart, formatChartCompact, toneColor, type ChartHover } from 'components/ui/chart'
import { useChartRange } from 'hooks/sto/useChartRange'
import { chartReadout } from 'models/chart'
import type { ProductChartView } from 'models/productChart'
import { DownloadCsv } from './PriceHistory'
import { ChartArea, ChartCard, ChartHeader } from './Performance.styles'
import { useChartHoverTracking } from 'hooks/sto/useStoTracking'

/** Equity (Figma 3329:68987): the underlying price as a chart; its data as the CSV. */
export const StockPerformance = ({ chart, history }: ProductChartView) => {
  const [hover, setHover] = useState<ChartHover | null>(null)
  const trackHover = useChartHoverTracking('performance')
  const onHover = useCallback(
    (next: ChartHover | null) => {
      setHover(next)
      trackHover(next)
    },
    [trackHover],
  )
  const [{ currency, points: all }] = chart.denominations
  const range = useChartRange(all, undefined, () => setHover(null))
  const readout = chartReadout(range.points, hover?.value)

  return (
    <ChartCard>
      <ChartArea>
        <ChartHeader>
          <ChartIdentity
            icon={chart.icon}
            symbol={chart.symbol}
            labelKey={chart.labelKey}
            currency={currency}
            readout={readout}
          />
          <RangePicker range={range} chart="performance" placement="header" />
        </ChartHeader>

        <PriceChart
          points={range.points}
          color={toneColor(readout.rangeTone)}
          height={AXIS_CHART_HEIGHT}
          showAxes
          formatValue={formatChartCompact}
          onHover={onHover}
          description={t('stoPage.heroChart.description', { symbol: chart.symbol })}
        />
        <RangePicker range={range} chart="performance" placement="below" />

        <ChartFootnote asOf={chart.asOf} source={chart.source}>
          <DownloadCsv history={history} symbol={chart.symbol} />
        </ChartFootnote>
      </ChartArea>
    </ChartCard>
  )
}
